import { useEffect, useState } from 'react';
import { ArrowLeft, Camera, ChevronRight } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CAPTURE_STATUS_TEXT, captureCount, captureCountLabel } from '@/components/jobs/job-text';
import { JobAttachments } from '@/components/jobs/JobAttachments';
import { readCachedJobs, rememberCurrentJob } from '@/components/jobs/current-job';
import { getCaptureJob, listJobCaptures, type CaptureJob, type CaptureQueueRow } from '@/lib/api';
import { useMyAccess } from '@/lib/access/access-context';
import { canCapture, CANNOT_CAPTURE_MESSAGE } from '@/lib/access/access-model';

/**
 * The job view (CAPTURE_JOBS.md section 6): what the manager asked for, in
 * full, the captures already taken for the job, and one primary action,
 * Add capture, which is the only way to the viewfinder. Every capture
 * belongs to a job from the moment it is taken, so the viewfinder route
 * carries this job's id.
 *
 * A capture row opens the result screen, where the operator can check what
 * was read while they can still pick the item up.
 */

type JobState = { kind: 'loading' } | { kind: 'loaded'; job: CaptureJob; stale: boolean } | { kind: 'failed' };

type CapturesState =
  | { kind: 'loading' }
  | { kind: 'loaded'; rows: CaptureQueueRow[]; cursor: string | null }
  | { kind: 'failed' };

function takenAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function JobView() {
  const { jobId = '' } = useParams();
  const navigate = useNavigate();
  const { access } = useMyAccess();
  const [jobState, setJobState] = useState<JobState>({ kind: 'loading' });
  const [captures, setCaptures] = useState<CapturesState>({ kind: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let live = true;
    getCaptureJob(jobId)
      .then((job) => {
        if (live) setJobState({ kind: 'loaded', job, stale: false });
      })
      .catch(() => {
        if (!live) return;
        // Offline, the copy My jobs last loaded still carries the full
        // instructions, which is what the operator opened this screen for.
        const cached = readCachedJobs()?.jobs.find((job) => job.jobId === jobId);
        setJobState(cached ? { kind: 'loaded', job: cached, stale: true } : { kind: 'failed' });
      });
    listJobCaptures(jobId)
      .then((page) => {
        if (live) setCaptures({ kind: 'loaded', rows: page.items, cursor: page.cursor ?? null });
      })
      .catch(() => {
        if (live) setCaptures({ kind: 'failed' });
      });
    return () => {
      live = false;
    };
  }, [jobId]);

  async function loadMore() {
    if (captures.kind !== 'loaded' || !captures.cursor) return;
    setLoadingMore(true);
    try {
      const page = await listJobCaptures(jobId, { cursor: captures.cursor });
      setCaptures({ kind: 'loaded', rows: [...captures.rows, ...page.items], cursor: page.cursor ?? null });
    } catch {
      // The rows already shown stay; the button stays for another try.
    } finally {
      setLoadingMore(false);
    }
  }

  function addCapture(job: CaptureJob) {
    rememberCurrentJob(job);
    navigate(`/jobs/${encodeURIComponent(job.jobId)}/capture`);
  }

  const backLink = (
    <Link
      to="/work"
      className="inline-flex min-h-11 w-fit items-center gap-1 text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      My jobs
    </Link>
  );

  if (jobState.kind === 'loading') {
    return (
      <div className="flex flex-col gap-3">
        {backLink}
        <p className="text-sm text-muted">Loading the job...</p>
      </div>
    );
  }

  if (jobState.kind === 'failed') {
    return (
      <div className="flex flex-col gap-3">
        {backLink}
        <p className="text-sm text-warning">
          This job could not be opened. It may have been removed, or you may no longer be assigned to it.
        </p>
      </div>
    );
  }

  const { job, stale } = jobState;
  const closed = job.status === 'CLOSED';
  const mayCapture = canCapture(access);
  const count = captures.kind === 'loaded' ? Math.max(captures.rows.length, captureCount(job)) : captureCount(job);

  return (
    <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-8">
      <div className="flex flex-col gap-5">
        {backLink}

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold">{job.name}</h1>
            {closed && <Badge>Closed</Badge>}
          </div>
          {stale && <p className="text-xs text-warning">Offline. This is the copy of the job last loaded on this device.</p>}
        </div>

        <section className="flex flex-col gap-2" aria-labelledby="job-instructions">
          <h2 id="job-instructions" className="text-sm font-semibold text-muted">
            Instructions
          </h2>
          {job.instructions.trim() ? (
            <p className="whitespace-pre-line text-sm leading-relaxed text-surface-foreground">{job.instructions}</p>
          ) : (
            <p className="text-sm text-muted">The manager left no instructions for this job.</p>
          )}
        </section>

        <JobAttachments jobId={job.jobId} />

        {/* On a phone the action sits straight under the instructions, in
            thumb reach. From the desktop breakpoint it moves to the side
            column beside them. */}
        <div className="lg:hidden">
          <AddCaptureAction closed={closed} mayCapture={mayCapture} onAdd={() => addCapture(job)} />
        </div>

        <section className="flex flex-col gap-3" aria-labelledby="job-captures">
          <h2 id="job-captures" className="text-sm font-semibold text-muted">
            Captures <span className="font-normal">({captureCountLabel(count).toLowerCase()})</span>
          </h2>

          {captures.kind === 'loading' && <p className="text-sm text-muted">Loading the captures...</p>}
          {captures.kind === 'failed' && (
            <p className="text-sm text-warning">The captures for this job could not be loaded.</p>
          )}
          {captures.kind === 'loaded' && captures.rows.length === 0 && (
            <p className="text-sm text-muted">Nothing has been captured for this job yet.</p>
          )}
          {captures.kind === 'loaded' && captures.rows.length > 0 && (
            <ul className="flex flex-col divide-y divide-border rounded-card border border-border">
              {captures.rows.map((row) => (
                <li key={row.captureId}>
                  <CaptureRow row={row} />
                </li>
              ))}
            </ul>
          )}
          {captures.kind === 'loaded' && captures.cursor && (
            <Button variant="outline" className="w-fit" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? 'Loading...' : 'Show more'}
            </Button>
          )}
        </section>
      </div>

      <aside className="hidden lg:sticky lg:top-4 lg:flex lg:flex-col lg:gap-3">
        <AddCaptureAction closed={closed} mayCapture={mayCapture} onAdd={() => addCapture(job)} />
      </aside>
    </div>
  );
}

function AddCaptureAction({
  closed,
  mayCapture,
  onAdd,
}: {
  closed: boolean;
  mayCapture: boolean;
  onAdd: () => void;
}) {
  if (!mayCapture) return <p className="text-sm text-muted">{CANNOT_CAPTURE_MESSAGE}</p>;
  if (closed) {
    return <p className="text-sm text-muted">This job is closed, so no more captures can be added to it.</p>;
  }
  return (
    <Button size="lg" className="w-full" onClick={onAdd}>
      <Camera className="size-5 shrink-0" />
      Add capture
    </Button>
  );
}

function CaptureRow({ row }: { row: CaptureQueueRow }) {
  const status = CAPTURE_STATUS_TEXT[row.status] ?? { label: row.status, tone: 'neutral' as const };
  return (
    <Link
      to={`/capture/${encodeURIComponent(row.captureId)}/result`}
      className="flex min-h-16 items-center gap-3 px-4 py-3 transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-surface-foreground">{row.headline}</p>
        <p className="mt-0.5 truncate text-xs text-muted">
          {row.capturedBy}
          {takenAt(row.createdAt) ? `, ${takenAt(row.createdAt)}` : ''}
        </p>
      </div>
      <Badge tone={status.tone} className="shrink-0">
        {status.label}
      </Badge>
      <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden="true" />
    </Link>
  );
}
